// Every number the sim reads lives here. Change a value, rebuild, test.
// Times in the sim are step counts (60 per second). Helpers at the bottom convert.
export const TICK_RATE = 60; // fixed steps per second, both sides
export const STEP_DT = 1 / TICK_RATE;
export const PATCH_MS = 1000 / 30; // snapshots per second from the server
export const INTERP_DELAY_MS = 100; // how far in the past other players are drawn
export const steps = (seconds) => Math.round(seconds * TICK_RATE);
// ---- movement
export const RUN_SPEED = 7.7; // m/s forward
export const BACK_STRAFE_FACTOR = 0.89; // backward and strafing speed as a share of forward
export const JUMP_HEIGHT = 2.45; // m
export const GRAVITY = 10; // m/s^2, gives 1.4 s in the air at that height
export const JUMP_VELOCITY = Math.sqrt(2 * GRAVITY * JUMP_HEIGHT);
export const AIR_ACCEL = 12; // m/s^2 toward the wish direction while airborne
export const AIR_SPEED_CAP = RUN_SPEED; // horizontal speed cannot grow past run speed in the air
export const TERMINAL_VELOCITY = 40; // m/s downward cap
export const CROUCH_SPEED_FACTOR = 0.5;
export const PUSH_DECAY = 6; // per second, how fast a knockback shove fades on the ground
export const PUSH_DECAY_AIR = 0.5;
export const PLAYER_HEIGHT = 2.1; // m, capsule end to end
export const PLAYER_RADIUS = 0.45;
export const CAPSULE_HALF_HEIGHT = (PLAYER_HEIGHT - 2 * PLAYER_RADIUS) / 2; // cylinder part
export const CAPSULE_CENTER = PLAYER_HEIGHT / 2; // above the feet
export const EYE_HEIGHT = 1.85;
export const CHEST_HEIGHT = 1.2;
export const HAND_FORWARD = 0.95; // where a grenade leaves the hand, ahead of the eyes and clear of the body
export const HAND_CLEARANCE = 0.3; // the hand stops this far short of a wall in the aim line
export const HAND_MIN = 0.15; // and never comes closer to the eyes than this
export const PITCH_LIMIT = 1.55; // radians, just short of straight up and down
export const KCC_OFFSET = 0.02; // character controller skin
export const STEP_MAX_HEIGHT = 0.35; // auto step height, a kerb not a crate
export const STEP_MIN_WIDTH = 0.25;
export const SNAP_TO_GROUND = 0.25;
export const MAX_SLOPE_RAD = (50 * Math.PI) / 180;
// ---- shield
export const SHIELD_MAX = 100;
export const SHIELD_REGEN_DELAY = steps(4.5); // no damage for this long and the bar refills
export const SHIELD_REGEN_TIME = steps(2.2); // full refill takes this long
export const SHIELD_REGEN_PER_STEP = SHIELD_MAX / SHIELD_REGEN_TIME;
// ---- grenade kinds. Slot 1 is Frag, slots 2 and 3 hold specials from pads.
export var Kind;
(function (Kind) {
    Kind[Kind["None"] = 0] = "None";
    Kind[Kind["Frag"] = 1] = "Frag";
    Kind[Kind["Impact"] = 2] = "Impact";
    Kind[Kind["Sticky"] = 3] = "Sticky";
    Kind[Kind["Bouncer"] = 4] = "Bouncer";
    Kind[Kind["Warhead"] = 5] = "Warhead";
})(Kind || (Kind = {}));
export const KIND_NAMES = ["", "FRAG", "IMPACT", "STICKY", "BOUNCER", "WARHEAD"];
export const KIND_COLORS = ["#ffffff", "#5EE22E", "#1ED0EE", "#FF3DBE", "#FFC81E", "#C0C6D0"];
export const KINDS = {
    [Kind.Frag]: { fuse: steps(3.0), cook: true, damage: 120, lethalR: 3.3, falloffR: 5.3, falloffDamage: 25, restitution: 0.3, friction: 0.7, radius: 0.12, sticky: false, contact: false, contactKill: false, padCount: 0, padRespawn: 0, clockFirst: 0, clockEvery: 0, knockback: 6 },
    [Kind.Impact]: { fuse: 0, cook: false, damage: 60, lethalR: 0, falloffR: 3.0, falloffDamage: 15, restitution: 0.0, friction: 1.0, radius: 0.12, sticky: false, contact: true, contactKill: true, padCount: 3, padRespawn: steps(30), clockFirst: 0, clockEvery: 0, knockback: 3 },
    [Kind.Sticky]: { fuse: steps(2.5), cook: true, damage: 120, lethalR: 3.5, falloffR: 5.6, falloffDamage: 25, restitution: 0.0, friction: 1.0, radius: 0.12, sticky: true, contact: false, contactKill: false, padCount: 2, padRespawn: 0, clockFirst: steps(30), clockEvery: steps(60), knockback: 6 },
    [Kind.Bouncer]: { fuse: steps(5.0), cook: true, damage: 120, lethalR: 4.0, falloffR: 6.4, falloffDamage: 25, restitution: 0.9, friction: 0.1, radius: 0.12, sticky: false, contact: false, contactKill: false, padCount: 2, padRespawn: steps(30), clockFirst: 0, clockEvery: 0, knockback: 6 },
    [Kind.Warhead]: { fuse: steps(3.0), cook: true, damage: 240, lethalR: 6.6, falloffR: 10.6, falloffDamage: 50, restitution: 0.3, friction: 0.7, radius: 0.24, sticky: false, contact: false, contactKill: false, padCount: 1, padRespawn: 0, clockFirst: steps(120), clockEvery: steps(120), knockback: 10 },
};
export const FRAG_COOLDOWN = steps(1.5);
export const THROW_SPEED = 20; // m/s, the one throw. Set by feel in the prototype
export const THROW_UP = 0.15; // added upward component so a level throw still lobs
export const GRENADE_LINEAR_DAMPING = 0.05;
export const GRENADE_ANGULAR_DAMPING = 0.5;
export const GRENADE_REST_SPEED = 0.4; // below this on the ground a grenade counts as resting
export const GRENADE_MAX = 40; // live grenades per room
export const KNOCKBACK_VERTICAL_CAP = JUMP_VELOCITY; // no grenade jumping
export const SELF_DAMAGE = true;
// ---- knife and kick
export const STAB_RANGE = 1.5; // m from the eye to the target's body, not to its centre
export const STAB_CONE_COS = Math.cos((50 * Math.PI) / 180); // and it has to be in front of you
export const STAB_CONE_MIN_DIST = 0.5; // closer than this the direction stops meaning anything
export const STAB_WINDUP = steps(0.25);
export const STAB_RECOVERY = steps(0.5);
export const STAB_REWIND_MS = 150;
export const KNIFE_THROW_SPEED = 22;
export const KNIFE_RETURN = steps(12);
export const KNIFE_PICKUP_R = 1.0;
export const KICK_RANGE = 2.0;
export const KICK_SPEED = 12;
export const KICK_UP = 0.25;
export const KICK_COOLDOWN = steps(1.0);
export const KICK_MAX_SPEED = 3; // m/s; a grenade moving faster than this is in flight and cannot be kicked
// ---- pads, drops, match
export const PAD_PICKUP_R = 1.0;
export const DROP_LIFETIME = steps(20);
export const LAST30_PAD_FACTOR = 2; // regular pads respawn this much faster in the last 30 s
export const SCORE_LIMIT = 20;
export const MATCH_LENGTH = steps(180);
export const OVERTIME_LENGTH = steps(30);
export const RESPAWN_TIME = steps(3);
export const RESULTS_TIME = steps(15);
export const LOBBY_COUNTDOWN = steps(20);
export const MIN_PLAYERS_TO_START = 2;
export const KILL_Y = -20; // fall below this and you respawn
export const MAX_PLAYERS = 8;
// Kill kinds that are not a grenade Kind: what the feed and the death card show.
export const KILL_FALL = 0;
export const KILL_STAB = 98;
export const KILL_KNIFE = 99;
export const RECONNECT_SECONDS = 20;
export const NAME_MAX = 16;
// ---- bots
export const BOT_REACTION = steps(0.4);
export const BOT_AIM_ERROR = 1.5; // m
export const BOT_COOK_MIN = steps(0.8);
export const BOT_COOK_MAX = steps(1.2);
export const BOT_SIGHT_MIN = 5;
export const BOT_SIGHT_MAX = 20;
export const BOT_MAX = 7;
//# sourceMappingURL=tuning.js.map